"use client"

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  WORLD_WIDTH,
  WORLD_HEIGHT,
  SPRITES,
  createInitialGameState,
  updateGame
} from './spaceinvaders/engine.js'
import { createAudio } from './spaceinvaders/audio.js'

export default function SpaceInvadersApp() {
  const canvasRef = useRef(null)
  const [gameState, setGameState] = useState('idle') // 'idle' | 'playing' | 'paused' | 'game_over'
  const [score, setScore] = useState(0)
  const [highScore, setHighScore] = useState(0)
  const [lives, setLives] = useState(3)
  const [wave, setWave] = useState(1)
  const [isMuted, setIsMuted] = useState(false)

  const gameRef = useRef(null)
  const audioRef = useRef(null)
  const inputRef = useRef({ left: false, right: false, shoot: false })
  const animFrameIdRef = useRef(null)
  const lastTimeRef = useRef(0)

  // Initialize audio and game state
  useEffect(() => {
    audioRef.current = createAudio()
    gameRef.current = createInitialGameState()
    setHighScore(gameRef.current.highScore)

    return () => {
      if (audioRef.current) {
        audioRef.current.destroy()
        audioRef.current = null
      }
      if (animFrameIdRef.current) {
        cancelAnimationFrame(animFrameIdRef.current)
      }
    }
  }, [])

  // Sync mute state
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.setMuted(isMuted)
    }
  }, [isMuted])

  // Pixel sprite drawing helper
  const drawSprite = (ctx, matrix, x, y, pixelSize, color) => {
    ctx.fillStyle = color
    for (let r = 0; r < matrix.length; r++) {
      const row = matrix[r]
      for (let c = 0; c < row.length; c++) {
        if (row[c] === '1') {
          ctx.fillRect(x + c * pixelSize, y + r * pixelSize, pixelSize, pixelSize)
        }
      }
    }
  }

  // Render loop
  const render = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const game = gameRef.current
    if (!game) return

    // Clear background
    ctx.fillStyle = '#0a0c10'
    ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT)

    // Draw subtle retro scanlines / star dots
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)'
    for (let i = 0; i < 30; i++) {
      const sx = (i * 97) % WORLD_WIDTH
      const sy = (i * 131) % (WORLD_HEIGHT - 60)
      ctx.fillRect(sx, sy, 1.5, 1.5)
    }

    // Draw Green Ground Line
    ctx.strokeStyle = '#10b981'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(10, WORLD_HEIGHT - 35)
    ctx.lineTo(WORLD_WIDTH - 10, WORLD_HEIGHT - 35)
    ctx.stroke()

    // 1. Draw Shields
    ctx.fillStyle = '#34d399'
    for (const shield of game.shields) {
      for (const block of shield.blocks) {
        if (block.alive) {
          ctx.fillRect(block.x, block.y, block.w + 0.3, block.h + 0.3)
        }
      }
    }

    // 2. Draw Invaders
    for (const inv of game.invaders) {
      if (!inv.alive) continue
      const spriteList = SPRITES[inv.type]
      const matrix = spriteList ? spriteList[game.animFrame] : SPRITES.squid[0]
      const pSize = 2
      drawSprite(ctx, matrix, inv.x, inv.y, pSize, inv.color)
    }

    // 3. Draw Mystery UFO
    if (game.ufo) {
      drawSprite(ctx, SPRITES.ufo, game.ufo.x, game.ufo.y, 2, '#ef4444')
    }

    // 4. Draw Player Laser
    if (game.playerLaser) {
      ctx.fillStyle = '#38bdf8'
      ctx.shadowColor = '#0284c7'
      ctx.shadowBlur = 4
      ctx.fillRect(game.playerLaser.x, game.playerLaser.y, game.playerLaser.w, game.playerLaser.h)
      ctx.shadowBlur = 0
    }

    // 5. Draw Alien Lasers
    ctx.fillStyle = '#f43f5e'
    for (const laser of game.alienLasers) {
      ctx.fillRect(laser.x, laser.y, laser.w, laser.h)
    }

    // 6. Draw Player Cannon
    if (game.player.alive) {
      drawSprite(ctx, SPRITES.cannon, game.player.x, game.player.y, 2, '#38bdf8')
    } else if (game.state === 'player_dying') {
      // Explosion fragments
      ctx.fillStyle = '#f43f5e'
      for (let i = 0; i < 8; i++) {
        const offset = Math.sin(i * 1.5) * 12
        ctx.fillRect(game.player.x + 10 + offset, game.player.y + 6 - offset, 3, 3)
      }
    }
  }, [])

  // Main Loop
  useEffect(() => {
    let active = true

    const loop = (timestamp) => {
      if (!active) return
      if (!lastTimeRef.current) lastTimeRef.current = timestamp
      const dt = Math.min((timestamp - lastTimeRef.current) / 1000, 0.1)
      lastTimeRef.current = timestamp

      if (gameRef.current) {
        updateGame(gameRef.current, dt, inputRef.current, audioRef.current)
        render()

        // Sync local React states for HUD
        setScore(gameRef.current.score)
        setHighScore(gameRef.current.highScore)
        setLives(gameRef.current.lives)
        setWave(gameRef.current.wave)

        if (gameRef.current.state === 'game_over' && gameState !== 'game_over') {
          setGameState('game_over')
        }
      }

      animFrameIdRef.current = requestAnimationFrame(loop)
    }

    animFrameIdRef.current = requestAnimationFrame(loop)
    return () => {
      active = false
      if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current)
    }
  }, [gameState, render])

  const startGame = () => {
    if (!gameRef.current) return
    audioRef.current?.init()
    gameRef.current = createInitialGameState()
    gameRef.current.state = 'playing'
    setGameState('playing')
  }

  const togglePause = () => {
    if (!gameRef.current) return
    if (gameState === 'playing') {
      gameRef.current.state = 'paused'
      setGameState('paused')
    } else if (gameState === 'paused') {
      gameRef.current.state = 'playing'
      setGameState('playing')
    }
  }

  // Keyboard handlers
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.repeat) return
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        inputRef.current.left = true
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        inputRef.current.right = true
      }
      if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
        inputRef.current.shoot = true
        if (gameState === 'idle') startGame()
      }
      if (e.key === 'p' || e.key === 'P' || e.key === 'Escape') {
        togglePause()
      }
    }

    const onKeyUp = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'a' || e.key === 'A') {
        inputRef.current.left = false
      }
      if (e.key === 'ArrowRight' || e.key === 'd' || e.key === 'D') {
        inputRef.current.right = false
      }
      if (e.key === ' ' || e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
        inputRef.current.shoot = false
      }
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [gameState])

  return (
    <div className="flex flex-col h-full bg-[#0a0c10] text-white select-none overflow-hidden font-mono">
      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-black/50 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-blue-500/20 text-blue-400">
            <i className="fas fa-space-shuttle text-base"></i>
          </div>
          <div>
            <h2 className="text-xs sm:text-sm font-bold tracking-wider text-white">SPACE INVADERS</h2>
            <div className="text-[10px] text-gray-400 flex gap-3">
              <span>SCORE: <strong className="text-white">{score}</strong></span>
              <span>HI: <strong className="text-amber-400">{highScore}</strong></span>
              <span>WAVE: <strong className="text-emerald-400">{wave}</strong></span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Remaining Lives Icons */}
          <div className="hidden sm:flex items-center gap-1.5 mr-2">
            <span className="text-[10px] text-gray-400">LIVES:</span>
            {Array.from({ length: Math.max(0, lives) }).map((_, i) => (
              <i key={i} className="fas fa-space-shuttle text-xs text-blue-400"></i>
            ))}
          </div>

          <button
            onClick={() => setIsMuted(m => !m)}
            className="p-1.5 text-xs rounded-md bg-white/10 hover:bg-white/20 text-gray-300 transition-colors border border-white/10"
            title={isMuted ? 'Unmute' : 'Mute'}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            <i className={`fas ${isMuted ? 'fa-volume-xmark text-rose-400' : 'fa-volume-high text-emerald-400'}`}></i>
          </button>

          {gameState === 'playing' || gameState === 'paused' ? (
            <button
              onClick={togglePause}
              className="px-2.5 py-1 text-xs rounded-md bg-white/10 hover:bg-white/20 text-gray-200 transition-colors border border-white/10"
            >
              {gameState === 'paused' ? 'Resume' : 'Pause'}
            </button>
          ) : null}

          <button
            onClick={startGame}
            className="px-2.5 py-1 text-xs rounded-md bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition-colors border border-blue-500/30 font-bold"
          >
            {gameState === 'idle' ? 'Start' : 'Restart'}
          </button>
        </div>
      </div>

      {/* Game Stage Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-2 relative overflow-hidden">
        <div className="relative w-full max-w-[480px] aspect-[4/5] max-h-[580px] bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10">
          <canvas
            ref={canvasRef}
            width={WORLD_WIDTH}
            height={WORLD_HEIGHT}
            className="w-full h-full object-contain block"
          />

          {/* Idle / Start Overlay */}
          {gameState === 'idle' && (
            <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center z-10">
              <h1 className="text-2xl sm:text-3xl font-black tracking-widest text-white mb-6">
                SPACE INVADERS
              </h1>
              <div className="space-y-2 mb-6 text-xs text-gray-300">
                <div className="flex items-center gap-3">
                  <span className="w-5 text-center text-red-500 font-bold">🛸</span>
                  <span>MYSTERY SAUCER = ??? PTS</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-5 text-center text-pink-400 font-bold">👾</span>
                  <span>SQUID INVADER = 30 PTS</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-5 text-center text-emerald-400 font-bold">🦀</span>
                  <span>CRAB INVADER = 20 PTS</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-5 text-center text-sky-400 font-bold">🐙</span>
                  <span>OCTOPUS INVADER = 10 PTS</span>
                </div>
              </div>
              <button
                onClick={startGame}
                className="px-6 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-400 text-black font-bold text-sm tracking-wider transition-colors shadow-lg"
              >
                PLAY NOW (SPACE)
              </button>
            </div>
          )}

          {/* Game Over Overlay */}
          {gameState === 'game_over' && (
            <div className="absolute inset-0 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center z-10">
              <h2 className="text-3xl font-black text-rose-500 tracking-wider mb-2">GAME OVER</h2>
              <p className="text-xs text-gray-300 mb-1">FINAL SCORE: <strong className="text-white text-sm">{score}</strong></p>
              <p className="text-xs text-amber-400 mb-6">BEST SCORE: <strong className="text-white text-sm">{highScore}</strong></p>
              <button
                onClick={startGame}
                className="px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-sm tracking-wider transition-colors shadow-lg"
              >
                PLAY AGAIN
              </button>
            </div>
          )}

          {/* Paused Overlay */}
          {gameState === 'paused' && (
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center z-10">
              <h2 className="text-2xl font-bold text-white tracking-widest mb-4">GAME PAUSED</h2>
              <button
                onClick={togglePause}
                className="px-5 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs tracking-wider transition-colors"
              >
                RESUME
              </button>
            </div>
          )}
        </div>

        {/* Mobile On-Screen Touch Controls */}
        <div className="w-full max-w-[480px] flex items-center justify-between px-4 py-2 mt-2 gap-4 shrink-0">
          <div className="flex gap-2">
            <button
              onPointerDown={(e) => { e.preventDefault(); inputRef.current.left = true }}
              onPointerUp={() => { inputRef.current.left = false }}
              onPointerCancel={() => { inputRef.current.left = false }}
              className="w-14 h-12 rounded-xl bg-white/10 active:bg-blue-500/40 border border-white/10 text-white flex items-center justify-center text-xl font-bold touch-none select-none"
              aria-label="Move Left"
            >
              ◀
            </button>
            <button
              onPointerDown={(e) => { e.preventDefault(); inputRef.current.right = true }}
              onPointerUp={() => { inputRef.current.right = false }}
              onPointerCancel={() => { inputRef.current.right = false }}
              className="w-14 h-12 rounded-xl bg-white/10 active:bg-blue-500/40 border border-white/10 text-white flex items-center justify-center text-xl font-bold touch-none select-none"
              aria-label="Move Right"
            >
              ▶
            </button>
          </div>

          <button
            onPointerDown={(e) => {
              e.preventDefault()
              if (gameState === 'idle') startGame()
              else inputRef.current.shoot = true
            }}
            onPointerUp={() => { inputRef.current.shoot = false }}
            onPointerCancel={() => { inputRef.current.shoot = false }}
            className="flex-1 max-w-[160px] h-12 rounded-xl bg-rose-500/30 active:bg-rose-500/60 border border-rose-400/40 text-rose-200 flex items-center justify-center gap-2 font-bold text-sm tracking-wider touch-none select-none"
            aria-label="Fire Missile"
          >
            <i className="fas fa-crosshairs text-base"></i>
            FIRE
          </button>
        </div>
      </div>
    </div>
  )
}
